// ロールベース権限制御カスタムフック
// Settings画面とアプリケーション全体での権限管理
// ADR-0021: Shared Account Model — owner固定（将来スタッフログイン対応時に再設計）

import {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
} from 'react';
import {
  UserRole,
  UserPermissions,
  DEFAULT_PERMISSIONS,
} from '../types/userManagement';

// 権限コンテキスト
interface PermissionContextType {
  currentUser: {
    id: string;
    role: UserRole;
    permissions: UserPermissions;
  } | null;
  hasPermission: (permission: keyof UserPermissions) => boolean;
  hasAnyPermission: (permissions: (keyof UserPermissions)[]) => boolean;
  hasAllPermissions: (permissions: (keyof UserPermissions)[]) => boolean;
  canAccessSettings: boolean;
  isLoading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | null>(null);

// 権限プロバイダー
interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    role: UserRole;
    permissions: UserPermissions;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error] = useState<string | null>(null);

  useEffect(() => {
    // ADR-0021: Shared Account Model — owner固定
    // 将来スタッフログインが必要になったら再設計（別Issue #65）
    setCurrentUser({
      id: 'owner',
      role: 'owner',
      permissions: DEFAULT_PERMISSIONS.owner,
    });
    setIsLoading(false);
  }, []);

  const hasPermission = (permission: keyof UserPermissions): boolean => {
    if (!currentUser) return false;
    return currentUser.permissions[permission] === true;
  };

  const hasAnyPermission = (
    permissions: (keyof UserPermissions)[]
  ): boolean => {
    if (!currentUser) return false;
    return permissions.some(
      (permission) => currentUser.permissions[permission] === true
    );
  };

  const hasAllPermissions = (
    permissions: (keyof UserPermissions)[]
  ): boolean => {
    if (!currentUser) return false;
    return permissions.every(
      (permission) => currentUser.permissions[permission] === true
    );
  };

  const canAccessSettings = hasPermission('canAccessSettings');

  const refreshPermissions = async () => {
    // No-op in owner-fixed mode
  };

  const contextValue: PermissionContextType = {
    currentUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessSettings,
    isLoading,
    error,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// 権限フック
// eslint-disable-next-line react-refresh/only-export-components
export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// 特定権限チェックフック
// eslint-disable-next-line react-refresh/only-export-components
export const usePermission = (permission: keyof UserPermissions): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
};

// 複数権限チェックフック
// eslint-disable-next-line react-refresh/only-export-components
export const useAnyPermission = (
  permissions: (keyof UserPermissions)[]
): boolean => {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissions);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAllPermissions = (
  permissions: (keyof UserPermissions)[]
): boolean => {
  const { hasAllPermissions } = usePermissions();
  return hasAllPermissions(permissions);
};

// ロール別権限チェック
// eslint-disable-next-line react-refresh/only-export-components
export const useRolePermissions = () => {
  const { currentUser } = usePermissions();

  const isOwner = currentUser?.role === 'owner';
  const isManager = currentUser?.role === 'manager';
  const isStaff = currentUser?.role === 'staff';
  const isAdmin = isOwner; // 互換性のため
  const isGeneral = isStaff; // 互換性のため

  const canManageUsers = isOwner;
  const canManageSettings = isOwner || isManager;
  const canViewReports = isOwner || isManager;
  const canManageCustomers = isOwner || isManager || isStaff;
  const canCreateReservations = isOwner || isManager || isStaff;

  return {
    currentUser,
    isOwner,
    isManager,
    isStaff,
    isAdmin,
    isGeneral,
    canManageUsers,
    canManageSettings,
    canViewReports,
    canManageCustomers,
    canCreateReservations,
  };
};

// Settings画面専用権限フック
// eslint-disable-next-line react-refresh/only-export-components
export const useSettingsPermissions = () => {
  const { hasPermission, currentUser } = usePermissions();

  return {
    // アカウント管理
    canViewAccountSettings: hasPermission('canAccessSettings'),
    canEditAccountSettings: hasPermission('canAccessSettings'),

    // ユーザー管理
    canViewUsers: hasPermission('canViewUsers'),
    canManageUsers: hasPermission('canManageUsers'),
    canInviteUsers: hasPermission('canInviteUsers'),

    // 店舗設定
    canViewStoreSettings: hasPermission('canViewStoreSettings'),
    canEditStoreSettings: hasPermission('canEditStoreSettings'),

    // システム設定
    canManageIntegrations: hasPermission('canManageIntegrations'),
    canExportData: hasPermission('canExportData'),

    // 現在のユーザー情報
    currentUserId: currentUser?.id,
    currentRole: currentUser?.role,
  };
};

// eslint-disable-next-line react-refresh/only-export-components
export default usePermissions;
